import {
  REPORT_SECTIONS,
  ENVIRONMENTAL_SECTIONS,
  TEST_FIT_SECTIONS,
  POWER_AVAILABILITY_SECTIONS,
  WATER_RESOURCE_SECTIONS,
  INTERCONNECTION_SECTIONS,
  PUBLIC_SENTIMENT_SECTIONS,
  CUSTOM_BRIEF_SECTIONS,
  EXECUTIVE_SUMMARY_REPORT_SECTIONS,
  SUPPLY_CHAIN_RISK_SECTIONS,
  RETIRED_SECTION_IDS,
  SECTION_ID_MIGRATIONS,
  STRIP_EXTENDED_DATA_REPORT_TYPES,
  migrateSectionIdForType,
} from '../config/reportSections';
import type { ReportTemplate, SectionConfig } from '../types/reportTemplate';
import type { ReportTypeId } from '../config/reportTypes';
import { normalizeStudioSectionConfigs } from './resolveStudioSectionList';

const ALL_SECTION_REGISTRIES = [
  REPORT_SECTIONS,
  ENVIRONMENTAL_SECTIONS,
  TEST_FIT_SECTIONS,
  POWER_AVAILABILITY_SECTIONS,
  WATER_RESOURCE_SECTIONS,
  INTERCONNECTION_SECTIONS,
  PUBLIC_SENTIMENT_SECTIONS,
  CUSTOM_BRIEF_SECTIONS,
  EXECUTIVE_SUMMARY_REPORT_SECTIONS,
  SUPPLY_CHAIN_RISK_SECTIONS,
];

const HEADING_KEY_ALIASES: Record<string, string> = (() => {
  const aliases: Record<string, string> = {};
  for (const registry of ALL_SECTION_REGISTRIES) {
    for (const section of registry) {
      aliases[normalizeHeadingKey(section.sectionId)] = section.sectionId;
    }
  }
  // Legacy section ids → canonical successors
  for (const [from, to] of Object.entries(SECTION_ID_MIGRATIONS)) {
    aliases[normalizeHeadingKey(from)] = to;
  }
  return aliases;
})();

/** Remap retired section ids to their successors (e.g. keyFindings → projectIntentOverview). */
export function migrateSectionId(id: string, reportType?: ReportTypeId | null): string {
  return migrateSectionIdForType(id, reportType);
}

function migrateSectionConfig(section: SectionConfig, reportType?: ReportTypeId | null): SectionConfig {
  const migrated = migrateSectionId(section.sectionId, reportType);
  return migrated === section.sectionId ? section : { ...section, sectionId: migrated };
}

function stripExtendedDataForType(section: SectionConfig, reportType: ReportTypeId): boolean {
  return !(STRIP_EXTENDED_DATA_REPORT_TYPES.has(reportType) && section.sectionId === 'extendedData');
}

function normalizeHeadingKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function normalizeHeadingOverrides(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [rawKey, rawValue] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof rawValue !== 'string') continue;
    const key = rawKey.trim();
    if (!key) continue;
    const canonical = HEADING_KEY_ALIASES[normalizeHeadingKey(key)] ?? key;
    out[canonical] = rawValue;
  }
  return out;
}

function isSectionConfigLike(value: unknown): value is SectionConfig {
  return (
    value != null
    && typeof value === 'object'
    && typeof (value as SectionConfig).sectionId === 'string'
    && (value as SectionConfig).sectionId.length > 0
  );
}

/**
 * Coerce persisted template section lists to arrays. Firestore / legacy
 * saves occasionally land as plain objects (`{ "0": {...}, "1": {...} }`)
 * or other non-array shapes — calling `.map` on those throws in Studio.
 */
export function coerceSectionConfigList(value: unknown): SectionConfig[] {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.filter(isSectionConfigLike);
  }
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).filter(isSectionConfigLike);
  }
  return [];
}

type CustomSectionEntry = NonNullable<ReportTemplate['customSections']>[number];

function isCustomSectionLike(value: unknown): value is CustomSectionEntry {
  return (
    value != null
    && typeof value === 'object'
    && typeof (value as CustomSectionEntry).id === 'string'
    && typeof (value as CustomSectionEntry).title === 'string'
  );
}

/** Coerce template customSections to an array (or undefined when absent/invalid). */
export function coerceCustomSectionEntries(
  value: unknown,
): ReportTemplate['customSections'] {
  if (value == null) return undefined;
  if (Array.isArray(value)) {
    const entries = value.filter(isCustomSectionLike);
    return entries.length > 0 ? entries : undefined;
  }
  if (typeof value === 'object') {
    if (isCustomSectionLike(value)) return [value];
    const entries = Object.values(value as Record<string, unknown>).filter(isCustomSectionLike);
    return entries.length > 0 ? entries : undefined;
  }
  return undefined;
}

function stripRetiredFromSections(sections: unknown, reportType: ReportTypeId = 'feasibility'): SectionConfig[] {
  return coerceSectionConfigList(sections)
    .map(s => migrateSectionConfig(s, reportType))
    .filter(s => stripExtendedDataForType(s, reportType))
    .filter((s) => !RETIRED_SECTION_IDS.has(s.sectionId));
}

/** Inject `toc` after executive summary when missing from a section list. */
export function injectMissingTocSection(sections: SectionConfig[]): SectionConfig[] {
  if (sections.some(s => s.sectionId === 'toc')) return sections;
  const execIdx = sections.findIndex(
    s => s.sectionId === 'executiveSummary' || s.sectionId === 'executive',
  );
  // Only inject when there is an executive-summary anchor to place the TOC after.
  // Report types without an exec summary (environmental, test-fit, interconnection…)
  // would otherwise get a TOC prepended at index 0 — before the cover/intro — which
  // is wrong. Templates that explicitly include a `toc` section keep it (guard above).
  if (execIdx < 0) return sections;
  const insertAt = execIdx + 1;
  const tocConfig: SectionConfig = {
    sectionId: 'toc',
    type: 'builtin',
    visible: true,
    columnLayout: 1,
    page: sections[execIdx]?.page ?? 1,
  };
  const next = [...sections];
  next.splice(insertAt, 0, tocConfig);
  return next;
}

function normalizeSectionsForReportType(
  reportType: ReportTypeId,
  sections: unknown,
): SectionConfig[] {
  const stripped = injectMissingTocSection(stripRetiredFromSections(sections, reportType));
  if (stripped.length === 0) return stripped;
  return normalizeStudioSectionConfigs(reportType, stripped);
}

function stripRetiredFromTemplate(template: ReportTemplate): ReportTemplate {
  const headingOverrides = { ...normalizeHeadingOverrides(template.headingOverrides) };
  for (const [from, to] of Object.entries(SECTION_ID_MIGRATIONS)) {
    if (headingOverrides[from] != null && headingOverrides[to] == null) {
      headingOverrides[to] = headingOverrides[from];
    }
    delete headingOverrides[from];
  }
  for (const id of RETIRED_SECTION_IDS) {
    delete headingOverrides[id];
  }

  const sectionVisibility = template.sectionVisibility
    ? Object.fromEntries(
      Object.entries(template.sectionVisibility)
        .map(([id, visible]) => [migrateSectionId(id), visible] as const)
        .filter(([id]) => !RETIRED_SECTION_IDS.has(id)),
    )
    : template.sectionVisibility;

  const pageLayouts = template.pageLayouts?.map((page) => ({
    ...page,
    sectionIds: (page.sectionIds ?? [])
      .map((id) => migrateSectionId(id))
      .filter((id) => !RETIRED_SECTION_IDS.has(id)),
  }));

  const flatSections = normalizeSectionsForReportType('feasibility', template.sections);
  const sectionsByReportTypeNormalized = template.sectionsByReportType
    ? Object.fromEntries(
      Object.entries(template.sectionsByReportType).map(([type, sections]) => [
        type,
        normalizeSectionsForReportType(type as ReportTypeId, sections),
      ]),
    )
    : template.sectionsByReportType;

  return {
    ...template,
    sections: flatSections,
    sectionsByReportType: sectionsByReportTypeNormalized,
    customSections: coerceCustomSectionEntries(template.customSections),
    pageLayouts,
    sectionOrder: template.sectionOrder
      ?.map((id) => migrateSectionId(id))
      .filter((id) => !RETIRED_SECTION_IDS.has(id)),
    headingOverrides,
    sectionVisibility,
  };
}

export function normalizeTemplateHeadingOverrides(template: ReportTemplate): ReportTemplate {
  return stripRetiredFromTemplate({
    ...template,
    headingOverrides: normalizeHeadingOverrides(template.headingOverrides),
  });
}
